import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, FormRadioGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { mapRefDataPickList } from 'shared/utils/Helpers';

export const BridgeCraneDesignDetails = React.forwardRef((props, ref) => {
  const {
    maxSpanOfBridge: savedMaxSpanOfBridge = '',
    maxHeightOfBridge: savedMaxHeightOfBridge = '',
    craneControlType: savedCraneControlType = '',
  } = props.value || {};

  const [maxSpanOfBridge, setMaxSpanOfBridge] = useState(savedMaxSpanOfBridge);
  const [maxHeightOfBridge, setMaxHeightOfBridge] = useState(savedMaxHeightOfBridge);
  const [craneControlType, setCraneControlType] = useState(savedCraneControlType);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.bridgeCrane;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.bridgeCrane;

  const craneControlTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.craneControlTypeOptions]);

  const validators = [
    {
      fieldName: 'maxSpanOfBridge',
      fieldValue: maxSpanOfBridge,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxHeightOfBridge',
      fieldValue: maxHeightOfBridge,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'craneControlType',
      fieldValue: craneControlType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isValid);
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      maxSpanOfBridge,
      maxHeightOfBridge,
      craneControlType,
    };

    props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [maxSpanOfBridge, maxHeightOfBridge, craneControlType]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <FormGroup
        id="maxSpanOfBridge"
        label={PAGE_CONSTANTS.maxSpanOfBridge.label}
        isRequired
        errorMessage={errors?.maxSpanOfBridge?.errorMessage}
        hasError={showErrors && errors?.maxSpanOfBridge?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'xl' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxSpanOfBridge.suffix}
          onChange={(e) => setMaxSpanOfBridge(e.target.value)}
          value={maxSpanOfBridge}
        />
      </FormGroup>

      <FormGroup
        id="maxHeightOfBridge"
        label={PAGE_CONSTANTS.maxHeightOfBridge.label}
        isRequired
        errorMessage={errors?.maxHeightOfBridge?.errorMessage}
        hasError={showErrors && errors?.maxHeightOfBridge?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxHeightOfBridge.suffix}
          onChange={(e) => setMaxHeightOfBridge(e.target.value)}
          value={maxHeightOfBridge}
        />
      </FormGroup>

      <FormRadioGroup
        id="craneControlType"
        name="craneControlType"
        vertical={true}
        options={craneControlTypeOptions}
        onChange={setCraneControlType}
        value={craneControlType}
        errorMessage={errors?.craneControlType?.errorMessage}
        hasError={showErrors && errors?.craneControlType?.invalid}
        legend={PAGE_CONSTANTS.craneControlType.label}
        margin={{ top: 'lg' }}
      />
    </>
  );
});

BridgeCraneDesignDetails.displayName = 'BridgeCraneDesignDetails';

BridgeCraneDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
